export const OPS_VOICE_METRIC_NAMES = [
  "voice_activated",
  "microphone_started",
  "realtime_ready",
  "realtime_reconnecting",
  "activity_started",
  "first_interim_transcript",
  "recognition_started",
  "interim_transcript",
  "final_transcript",
  "transcript_revised",
  "brain_request_started",
  "brain_transport_open",
  "brain_heartbeat",
  "approval_consent_required",
  "approval_consent_armed",
  "approval_consent_result",
  "answer_visible",
  "assistant_audio_started",
  "speech_queued",
  "acknowledgment_frame_received",
  "acknowledgment_audio_frame_received",
  "acknowledgment_audio_scheduled",
  "acknowledgment_receiver_started",
  "first_audible_speech",
  "speech_boundary",
  "speech_ended",
  "user_interrupted",
  "listening_rearmed",
  "voice_error"
] as const;

export type OpsVoiceMetricName = typeof OPS_VOICE_METRIC_NAMES[number];

const LOCAL_FAILURE_OWNERS = ["status_cue", "acknowledgment_audio", "assistant_audio", "transport"] as const;
const LOCAL_FAILURE_SITES = [
  "capture_packet_bound", "capture_queue_bound", "control_queue_bound",
  "control_drain_timeout", "interruption_control_bound", "audio_graph_closed",
  "audio_decode_failed", "audio_schedule_failed", "audio_never_audible", "status_cue_schedule_failed"
] as const;
export interface OpsVoiceLocalFailure {
  failureOwner?: typeof LOCAL_FAILURE_OWNERS[number];
  site?: typeof LOCAL_FAILURE_SITES[number];
  captureFrames?: number;
  controlFrames?: number;
  socketBufferedBytes?: number;
  packetBytes?: number;
  drainAgeMs?: number;
  inputMuted?: boolean;
}

function safeLocalFailure(value: OpsVoiceLocalFailure | undefined): OpsVoiceLocalFailure | undefined {
  if (!value || typeof value !== "object") return undefined;
  const result: OpsVoiceLocalFailure = {};
  if (LOCAL_FAILURE_OWNERS.includes(value.failureOwner!)) result.failureOwner = value.failureOwner!;
  if (LOCAL_FAILURE_SITES.includes(value.site!)) result.site = value.site!;
  for (const key of ["captureFrames", "controlFrames", "socketBufferedBytes", "packetBytes", "drainAgeMs"] as const) {
    const count = value[key];
    if (typeof count === "number" && Number.isFinite(count) && count >= 0) {
      result[key] = Math.min(0x7fffffff, Math.floor(count));
    }
  }
  if (typeof value.inputMuted === "boolean") result.inputMuted = value.inputMuted;
  return Object.keys(result).length > 0 ? result : undefined;
}

export interface OpsVoiceMetric {
  turnId: string;
  name: OpsVoiceMetricName;
  atMs: number;
  elapsedFromFinalMs?: number;
  status?: "ok" | "blocked" | "failed";
  safeDetail?: string;
  localFailure?: OpsVoiceLocalFailure;
}

type OpsVoiceMetricWindow = Window & { __opsVoiceMetrics?: OpsVoiceMetric[] };

export function createOpsVoiceTurnId(): string {
  const suffix = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
  return `ops-voice-${Date.now()}-${suffix}`;
}

export function recordOpsVoiceMetric(
  metric: Omit<OpsVoiceMetric, "atMs"> & { atMs?: number }
): void {
  if (typeof window === "undefined" || !OPS_VOICE_METRIC_NAMES.includes(metric.name)) return;
  const target = window as OpsVoiceMetricWindow;
  const localFailure = metric.name === "voice_error" ? safeLocalFailure(metric.localFailure) : undefined;
  // WHY [BOL-VOICE-METRICS-PRIVATE-001]: production latency proof needs actual
  // browser clocks, not source constants. Keep only turn IDs, stage names, safe
  // status, and timing—never transcript, answer text, cookies, or tokens.
  // Falsifier: the ledger exposes any spoken or business content.
  const entry: OpsVoiceMetric = {
    turnId: metric.turnId,
    name: metric.name,
    atMs: Number.isFinite(metric.atMs) ? metric.atMs! : Math.round(performance.now()),
    ...(typeof metric.elapsedFromFinalMs === "number"
      ? { elapsedFromFinalMs: Math.max(0, Math.round(metric.elapsedFromFinalMs)) }
      : {}),
    ...(metric.status ? { status: metric.status } : {}),
    ...(metric.safeDetail ? { safeDetail: metric.safeDetail.slice(0, 80) } : {}),
    ...(localFailure ? { localFailure } : {})
  };
  target.__opsVoiceMetrics = [...(target.__opsVoiceMetrics ?? []), entry].slice(-160);
}

export function readOpsVoiceMetrics(): OpsVoiceMetric[] {
  if (typeof window === "undefined") return [];
  return [...((window as OpsVoiceMetricWindow).__opsVoiceMetrics ?? [])];
}
